/**
 * Open Dialog Card
 *
 * Öffnet einen bestehenden UI-Dialog aus dem Chat heraus, statt dessen Fragen ein zweites
 * Mal als Chat-Karte zu bauen. Die Mindmap verlangt das an fünf Stellen ausdrücklich
 * („bitte das bestehende Popup-Fenster nutzen"); der eigentliche Grund ist Ticket
 * `e9a194e2`: zwei Fassungen derselben Frage prüfen irgendwann verschiedene Felder.
 *
 * Die Karte selbst öffnet nichts — sie legt die Anfrage in den Store. Der Dialog-Wirt
 * hängt im App-Rahmen und überlebt dadurch das Neuladen des Verlaufs; ein Dialog direkt
 * aus der Nachricht heraus würde mit ihr verschwinden.
 */

import React from 'react';
import { Box, Paper, Typography, Button } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import useAIChatStore from '../../../stores/ai-chat-store';

export interface OpenDialogCardData {
  dialog: 'create_campaign' | 'campaign_wizard' | 'blacklist';
  props?: Record<string, unknown>;
  label?: string;
}

const OpenDialogCard: React.FC<{ data: OpenDialogCardData }> = ({ data }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const requestDialog = useAIChatStore((state) => state.requestDialog);

  const label = data.label || t('openDialogDefault', 'Fenster öffnen');

  const handleOpen = () => {
    requestDialog({
      dialog: data.dialog,
      props: data.props || {},
      // Ohne eigene Kennung öffnet ein zweiter Klick auf dieselbe Karte nichts mehr,
      // weil sich der Zustand nicht ändert.
      requestId: `${data.dialog}-${Date.now()}`,
    });
  };

  return (
    <Paper className={classes.card} elevation={0}>
      <Box className={classes.row}>
        <Button variant="contained" size="small" startIcon={<OpenInNewIcon />} onClick={handleOpen}>
          {label}
        </Button>
      </Box>
      <Typography variant="caption" color="textSecondary">
        {data.dialog === 'campaign_wizard'
          ? t(
              'openWizardHint',
              'Öffnet den Assistenten „Neue Kampagne“ — Name und Absender sind schon vorbelegt, soweit bekannt.'
            )
          : t('openDialogHint', 'Öffnet das gewohnte Fenster — deine Angaben aus dem Gespräch sind schon eingetragen.')}
      </Typography>
    </Paper>
  );
};

const useStyles = makeStyles()((theme) => ({
  card: {
    padding: theme.spacing(1.5),
    marginTop: theme.spacing(1),
    border: `1px solid ${theme.palette.primary.main}33`,
    borderRadius: theme.shape.borderRadius,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    maxWidth: 520,
    backgroundColor: theme.palette.mode === 'dark'
      ? theme.palette.background.paper
      : `${theme.palette.primary.main}06`,
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
}));

export default OpenDialogCard;
